import { createContext, useCallback, useContext, useRef, useState, ReactNode } from 'react';
import { CheckCircle2, AlertCircle, Loader2, X } from 'lucide-react';

type ToastKind = 'loading' | 'success' | 'error';
type Toast = { id: string; kind: ToastKind; title: string; detail?: string };
type Ctx = {
  push: (t: Omit<Toast, 'id'>) => string;
  update: (id: string, patch: Partial<Omit<Toast, 'id'>>) => void;
  dismiss: (id: string) => void;
  /** Helper one-shot: lance une promesse et gère loading/success/error. */
  promise: <T,>(p: Promise<T>, msgs: { loading: string; success: string | ((v: T) => string); error?: string | ((e: unknown) => string) }) => Promise<T>;
};

const ToastCtx = createContext<Ctx | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const counter = useRef(0);

  const dismiss = useCallback((id: string) => {
    setToasts((t) => t.filter((x) => x.id !== id));
  }, []);

  const push = useCallback((t: Omit<Toast, 'id'>) => {
    const id = `t${++counter.current}`;
    setToasts((arr) => [...arr, { id, ...t }]);
    if (t.kind !== 'loading') {
      setTimeout(() => dismiss(id), t.kind === 'error' ? 6000 : 3500);
    }
    return id;
  }, [dismiss]);

  const update = useCallback((id: string, patch: Partial<Omit<Toast, 'id'>>) => {
    setToasts((arr) => arr.map((t) => t.id === id ? { ...t, ...patch } : t));
    if (patch.kind && patch.kind !== 'loading') {
      setTimeout(() => dismiss(id), patch.kind === 'error' ? 6000 : 3500);
    }
  }, [dismiss]);

  const promise = useCallback(<T,>(p: Promise<T>, msgs: { loading: string; success: string | ((v: T) => string); error?: string | ((e: unknown) => string) }) => {
    const id = push({ kind: 'loading', title: msgs.loading });
    return p.then((v) => {
      update(id, { kind: 'success', title: typeof msgs.success === 'function' ? msgs.success(v) : msgs.success });
      return v;
    }).catch((e) => {
      const errMsg = msgs.error ? (typeof msgs.error === 'function' ? msgs.error(e) : msgs.error)
        : (e?.response?.data?.message ?? e?.message ?? 'Erreur inconnue');
      update(id, { kind: 'error', title: errMsg });
      throw e;
    });
  }, [push, update]);

  return (
    <ToastCtx.Provider value={{ push, update, dismiss, promise }}>
      {children}
      <div className="fixed bottom-4 right-4 z-[100] flex flex-col gap-2 max-w-sm pointer-events-none">
        {toasts.map((t) => (
          <div
            key={t.id}
            className={`pointer-events-auto card flex items-start gap-2 px-3 py-2.5 shadow-lg border-l-2 ${
              t.kind === 'success' ? 'border-l-success'
              : t.kind === 'error' ? 'border-l-danger'
              : 'border-l-info'
            }`}
            style={{ minWidth: 260, animation: 'slideIn .18s ease-out' }}
          >
            {t.kind === 'loading' && <Loader2 size={14} className="text-info shrink-0 mt-0.5 animate-spin" />}
            {t.kind === 'success' && <CheckCircle2 size={14} className="text-success shrink-0 mt-0.5" />}
            {t.kind === 'error'   && <AlertCircle  size={14} className="text-danger shrink-0 mt-0.5" />}
            <div className="flex-1 min-w-0">
              <div className="text-xs font-medium">{t.title}</div>
              {t.detail && <div className="text-[11px] text-text-muted mt-0.5">{t.detail}</div>}
            </div>
            <button onClick={() => dismiss(t.id)} className="text-text-faint hover:text-text shrink-0">
              <X size={12} />
            </button>
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}

export function useToast(): Ctx {
  const ctx = useContext(ToastCtx);
  if (!ctx) throw new Error('useToast must be used within <ToastProvider>');
  return ctx;
}
